import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Building, Search, Users, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ClassroomManagement = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    capacity: '',
    location: ''
  });

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      toast.error('Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null
      };
      
      if (editingClassroom) {
        // Merge with existing classroom data to preserve all fields
        const mergedData = {
          ...editingClassroom,  // Start with all existing classroom data
          ...submitData,        // Override with form data
          id: editingClassroom.id // Preserve ID
        };
        
        console.log('[UPDATE CLASSROOM] Merged data being sent:', mergedData);
        
        await axios.put(`${API}/classrooms/${editingClassroom.id}`, mergedData);
        toast.success('Classroom updated successfully');
      } else {
        await axios.post(`${API}/classrooms`, submitData);
        toast.success('Classroom created successfully');
      }
      
      setIsModalOpen(false);
      setEditingClassroom(null);
      setFormData({ name: '', capacity: '', location: '' });
      fetchClassrooms();
    } catch (error) {
      console.error('Error saving classroom:', error);
      toast.error('Failed to save classroom');
    }
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setFormData({
      name: classroom.name,
      capacity: classroom.capacity ? classroom.capacity.toString() : '',
      location: classroom.location || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this classroom?')) {
      try {
        await axios.delete(`${API}/classrooms/${id}`);
        toast.success('Classroom deleted successfully');
        fetchClassrooms();
      } catch (error) {
        console.error('Error deleting classroom:', error);
        toast.error('Failed to delete classroom');
      }
    }
  };

  const openCreateModal = () => {
    setEditingClassroom(null);
    setFormData({ name: '', capacity: '', location: '' });
    setIsModalOpen(true);
  };

  const filteredClassrooms = classrooms.filter(classroom =>
    classroom.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (classroom.location && classroom.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Classroom Management</h1>
          <p className="text-gray-600">Manage your classrooms and facilities</p>
        </div>
        
        <Button onClick={openCreateModal} className="btn-primary flex items-center" data-testid="create-classroom-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Classroom
        </Button>
      </div>

      {/* Search */}
      <Card className="card-enhanced">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search classrooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 form-input"
              data-testid="search-classrooms"
            />
          </div>
        </CardContent>
      </Card>

      {/* Classrooms Grid */}
      {filteredClassrooms.length === 0 ? (
        <Card className="card-enhanced">
          <CardContent className="p-12 text-center">
            <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No classrooms found' : 'No classrooms yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? 'Try adjusting your search terms' : 'Add your first classroom to get started'}
            </p>
            {!searchTerm && (
              <Button onClick={openCreateModal} className="btn-primary" data-testid="empty-state-create-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add First Classroom
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClassrooms.map((classroom) => (
            <Card key={classroom.id} className="card-enhanced hover-lift" data-testid={`classroom-card-${classroom.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                      {classroom.name}
                    </CardTitle>
                    
                    {classroom.capacity && (
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <Users className="w-3 h-3 mr-1" />
                        <span>Capacity: {classroom.capacity} students</span>
                      </div>
                    )}
                    
                    {classroom.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-3 h-3 mr-1" />
                        <span className="truncate">{classroom.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-2 bg-orange-50 rounded-lg">
                    <Building className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created: {new Date(classroom.created_at).toLocaleDateString()}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(classroom)}
                      className="p-2 h-8 w-8"
                      data-testid={`edit-classroom-${classroom.id}`}
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(classroom.id)}
                      className="p-2 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-classroom-${classroom.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="classroom-modal-title">
              {editingClassroom ? 'Edit Classroom' : 'Add New Classroom'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Classroom Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                placeholder="Enter classroom name"
                required
                data-testid="classroom-name-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capacity (students)
              </label>
              <Input
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                className="form-input"
                placeholder="Enter seating capacity (optional)"
                min="1"
                data-testid="classroom-capacity-input"
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <Input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="form-input"
                placeholder="Enter classroom location (optional)"
                data-testid="classroom-location-input"
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="flex-1"
                data-testid="cancel-classroom-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-primary"
                data-testid="save-classroom-btn"
              >
                {editingClassroom ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassroomManagement;